import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './PolaroidExpanded.module.css';

function formatCaption(id) {
  return `N · ${id.replace('N', '').split('').join(' ')}`;
}

// Deterministic film metadata derived from photo id
function getFilmData(id) {
  const n = parseInt(id.replace('N', ''), 10);
  const apertures = ['ƒ/1.4', 'ƒ/1.8', 'ƒ/2.0', 'ƒ/2.8', 'ƒ/1.2'];
  const shutters  = ['1/250s', '1/500s', '1/125s', '1/320s', '1/200s'];
  const isos      = ['ISO 3200', 'ISO 6400', 'ISO 1600', 'ISO 3200', 'ISO 6400'];
  const lenses    = ['50MM SUMMILUX', '35MM NOCTILUX', '85MM APO', '28MM ELMARIT', '50MM APO'];
  return {
    aperture: apertures[n % apertures.length],
    shutter:  shutters [n % shutters .length],
    iso:      isos     [n % isos     .length],
    lens:     lenses   [n % lenses   .length],
  };
}

export default function PolaroidExpanded({ photo, photos, initialIndex, originRect, originRotation, onClose }) {
  const [phase,       setPhase]       = useState('origin'); // 'origin' | 'expanded' | 'closing'
  const [aspect,      setAspect]      = useState(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex ?? 0);
  const [fadingPhoto, setFadingPhoto] = useState(false);
  const filmstripRef  = useRef(null);
  const activeFilmRef = useRef(null);

  const total       = photos ? photos.length : 1;
  const activePhoto = (photos && photos[activeIndex]) ?? photo;
  const filmData    = getFilmData(activePhoto.id);
  const photoCount  = total > 1
    ? `${String(activeIndex + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`
    : '';

  // ── FLIP entrance ─────────────────────────────────────────────────────────
  useEffect(() => {
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => setPhase('expanded'));
      return () => cancelAnimationFrame(id2);
    });
    return () => cancelAnimationFrame(id1);
  }, []);

  // ── Reset aspect on photo change ──────────────────────────────────────────
  useEffect(() => {
    setAspect(null);
  }, [activeIndex]);

  // ── Auto-scroll filmstrip to keep active thumb visible ────────────────────
  useEffect(() => {
    if (!filmstripRef.current || !activeFilmRef.current || phase !== 'expanded') return;
    const container = filmstripRef.current;
    const item      = activeFilmRef.current;
    const cRect     = container.getBoundingClientRect();
    const iRect     = item.getBoundingClientRect();
    const scrollTo  = container.scrollLeft + (iRect.left - cRect.left) - (cRect.width / 2) + (iRect.width / 2);
    container.scrollTo({ left: Math.max(0, scrollTo), behavior: 'smooth' });
  }, [activeIndex, phase]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    setPhase('closing');
    setTimeout(onClose, 450);
  }, [onClose]);

  const navigate = useCallback((newIndex) => {
    if (total <= 1) return;
    setFadingPhoto(true);
    setTimeout(() => {
      setActiveIndex(newIndex);
      setFadingPhoto(false);
    }, 160);
  }, [total]);

  const handlePrev = useCallback((e) => {
    e?.stopPropagation();
    navigate((activeIndex - 1 + total) % total);
  }, [activeIndex, total, navigate]);

  const handleNext = useCallback((e) => {
    e?.stopPropagation();
    navigate((activeIndex + 1) % total);
  }, [activeIndex, total, navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape')      handleClose();
      if (e.key === 'ArrowLeft')   handlePrev();
      if (e.key === 'ArrowRight')  handleNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose, handlePrev, handleNext]);

  // ── Photo sizing ──────────────────────────────────────────────────────────
  // Available vertical space = viewport − top letterbox (44) − filmstrip (68)
  //   − meta strip (~36) − gap (~20) − frame padding (14) − caption (40) = ~222
  const maxPhotoH = Math.max(280, window.innerHeight - 240);
  const maxPhotoW = window.innerWidth * 0.82;
  const targetW = aspect && aspect < 0.9
    ? Math.min(maxPhotoH * aspect + 28, window.innerWidth * 0.40, maxPhotoW)
    : Math.min(maxPhotoW, maxPhotoH * (aspect || 1.5) + 28, 860);

  const originCenterX = originRect.left + originRect.width  / 2;
  const originCenterY = originRect.top  + originRect.height / 2;
  const targetCenterX = window.innerWidth  / 2;
  // Center between top letterbox and filmstrip bottom
  const targetCenterY = 44 + (window.innerHeight - 44 - 68) / 2;
  const atOrigin      = phase === 'origin' || phase === 'closing';
  const isExpanded    = phase === 'expanded';

  const polaroidStyle = {
    left:  `${atOrigin ? originCenterX : targetCenterX}px`,
    top:   `${atOrigin ? originCenterY : targetCenterY}px`,
    width: `${atOrigin ? originRect.width : targetW}px`,
    transform: `translate(-50%, -50%) rotate(${atOrigin ? originRotation : 0}deg)`,
    transition: phase === 'origin'
      ? 'none'
      : 'left 450ms var(--ease-out-cine), top 450ms var(--ease-out-cine), width 450ms var(--ease-out-cine), transform 450ms var(--ease-out-cine)',
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isExpanded ? styles.open : ''}`}
        data-backdrop
        onClick={handleClose}
      />

      {/* Top letterbox bar */}
      <div className={`${styles.letterboxTop} ${isExpanded ? styles.letterboxIn : ''}`} aria-hidden="true" />

      {/* Close button */}
      <button
        className={`${styles.closeBtn} ${isExpanded ? styles.closeBtnVisible : ''}`}
        onClick={handleClose}
        aria-label="Close"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <line x1="3" y1="3" x2="17" y2="17" stroke="currentColor" strokeWidth="0.9" />
          <line x1="17" y1="3" x2="3"  y2="17" stroke="currentColor" strokeWidth="0.9" />
        </svg>
      </button>

      {/* Photo counter — top left */}
      {total > 1 && (
        <div className={`${styles.counter} ${isExpanded ? styles.counterVisible : ''}`} aria-hidden="true">
          {photoCount}
        </div>
      )}

      {/* Left / right navigation arrows */}
      {total > 1 && (
        <>
          <button
            className={`${styles.navBtn} ${styles.navBtnLeft} ${isExpanded ? styles.navBtnVisible : ''}`}
            onClick={handlePrev}
            aria-label="Previous photo"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <polyline points="17 21 10 14 17 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className={`${styles.navBtn} ${styles.navBtnRight} ${isExpanded ? styles.navBtnVisible : ''}`}
            onClick={handleNext}
            aria-label="Next photo"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <polyline points="11 7 18 14 11 21" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </>
      )}

      {/* Film metadata strip */}
      <div className={`${styles.meta} ${isExpanded ? styles.metaVisible : ''}`} aria-hidden="true">
        <span>{filmData.aperture}</span>
        <span className={styles.dot}>·</span>
        <span>{filmData.shutter}</span>
        <span className={styles.dot}>·</span>
        <span>{filmData.iso}</span>
        <span className={styles.dot}>·</span>
        <span>{filmData.lens}</span>
        <span className={styles.dot}>·</span>
        <span className={styles.metaId}>{formatCaption(activePhoto.id)}</span>
      </div>

      {/* Bottom filmstrip — replaces letterbox bottom */}
      {total > 1 && (
        <div
          ref={filmstripRef}
          className={`${styles.filmstrip} ${isExpanded ? styles.filmstripVisible : ''}`}
          aria-hidden="true"
        >
          {photos.map((p, i) => (
            <div
              key={p.id}
              ref={i === activeIndex ? activeFilmRef : null}
              className={`${styles.filmItem} ${i === activeIndex ? styles.filmItemActive : ''}`}
              onClick={(e) => { e.stopPropagation(); navigate(i); }}
            >
              <img
                src={p.src}
                alt=""
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>
      )}

      {/* Polaroid */}
      <div className={styles.polaroid} style={polaroidStyle} data-polaroid>
        <div className={styles.frame}>
          <img
            key={activePhoto.id}
            src={activePhoto.full ?? activePhoto.src}
            alt=""
            className={styles.photo}
            style={{
              opacity: fadingPhoto ? 0 : 1,
              transition: fadingPhoto ? 'opacity 140ms ease' : 'opacity 220ms ease 60ms',
            }}
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.target;
              if (naturalHeight > 0) setAspect(naturalWidth / naturalHeight);
            }}
          />
          <div className={styles.strip}>
            <span className={styles.caption}>{formatCaption(activePhoto.id)}</span>
          </div>
        </div>
      </div>
    </>
  );
}
